import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "../../context/AuthContext";
import { useData } from "../../context/DataContext";
import {
  getHrAdvanceApprovalContext,
  approveAdvance,
  rejectAdvance,
  referAdvanceToGm,
} from "../../api/advances";
import { mapAdvanceRow } from "../../utils/dataMappers";
import { canHrApproveRequests, isHrFamilyRole } from "../../utils/hrStaffHelpers";
import AttachmentList from "../common/AttachmentList";
import EmployeePunchDaysModal from "../common/EmployeePunchDaysModal";
import { exportExcel } from "../reports/reportExport";
import {
  calendarMonthYearFromDate,
  toIsoDate,
} from "../../utils/fillMonthPunchDays";
import {
  deductionMonthLabels,
  scheduleForApproval,
  scheduleMatchesLoan,
  splitInstallmentAmounts,
} from "../../utils/exceptionalInstallmentSchedule";
import "./HrAdvanceApprovals.css";

function formatAmount(amount) {
  const value = Number(amount);
  if (Number.isNaN(value)) return "—";
  return `${value.toLocaleString("ar-EG")} ج.م`;
}

function formatNumber(value) {
  const num = Number(value);
  if (Number.isNaN(num)) return "—";
  return num.toLocaleString("ar-EG");
}

function formatPercent(value) {
  const num = Number(value);
  if (value == null || value === "" || Number.isNaN(num)) return "—";
  return `${num.toLocaleString("ar-EG", {
    maximumFractionDigits: 2,
  })}%`;
}

const ADVANCE_SALARY_CAP_PERCENT = 50;

/** Live salary % when HR types an approved amount (received this month + new cash). */
function resolveSalaryPercentage(row, approvedRaw) {
  const salary = Number(row.monthlySalary) || 0;
  if (salary <= 0) return row.salaryPercentage;

  const approved = Number(approvedRaw);
  if (
    approvedRaw == null ||
    approvedRaw === "" ||
    Number.isNaN(approved) ||
    approved <= 0
  ) {
    return row.salaryPercentage;
  }

  const received = Number(row.dueAdvancesAmount) || 0;
  return Math.round(((received + approved) / salary) * 10000) / 100;
}

function exceedsSalaryCap(percentage) {
  return percentage != null && percentage > ADVANCE_SALARY_CAP_PERCENT;
}

function salaryCapWarning(row, approvedRaw) {
  if (!(Number(row.monthlySalary) > 0)) return "";
  if (!exceedsSalaryCap(resolveSalaryPercentage(row, approvedRaw))) return "";
  return `تحذير: المستلم + المعتمد يتجاوز ${ADVANCE_SALARY_CAP_PERCENT}% من الراتب (الحد الأقصى ${formatAmount(row.maxAdvanceAmount)}). يمكن تأكيد الموافقة.`;
}

function formatDate(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString("ar-EG", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

export default function HrAdvanceApprovals({ teamOnly = false } = {}) {
  const { user } = useAuth();
  const { teamMembers, employeesByBranch, refreshData } = useData();
  const canApprove = canHrApproveRequests(user?.role);
  const isGm = user?.role === "gm";
  const canReferToGm =
    !isGm && (isHrFamilyRole(user?.role) || user?.role === "admin");
  const [rows, setRows] = useState([]);
  const [approvedAmounts, setApprovedAmounts] = useState({});
  const [editedInstallments, setEditedInstallments] = useState({});
  const [installmentSchedules, setInstallmentSchedules] = useState({});
  const [monthMeta, setMonthMeta] = useState({ month: null, year: null });
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [actionModal, setActionModal] = useState({
    open: false,
    type: "",
    advanceId: null,
    notes: "",
  });
  const [actionError, setActionError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [punchModal, setPunchModal] = useState(null);

  const teamUserIds = useMemo(() => {
    const ids = new Set();
    (teamMembers || []).forEach((emp) => {
      if (emp.userId) ids.add(String(emp.userId));
    });
    return ids;
  }, [teamMembers]);

  const loadAdvances = useCallback(async () => {
    setLoading(true);
    setLoadError("");
    try {
      const context = await getHrAdvanceApprovalContext();
      const items = Array.isArray(context?.items) ? context.items : [];
      const mapped = items.map((item) => {
        const base = mapAdvanceRow(item);
        return {
          ...base,
          jobTitle: item.jobTitle ?? null,
          requestedAmount: Number(
            item.requestedAmount ?? item.amount ?? base.amount ?? 0,
          ),
          monthlySalary: Number(item.monthlySalary ?? 0),
          dueAdvancesAmount: Number(item.dueAdvancesAmount ?? 0),
          salaryPercentage:
            item.salaryPercentage == null || item.salaryPercentage === ""
              ? null
              : Number(item.salaryPercentage),
          maxAdvanceAmount: Number(item.maxAdvanceAmount ?? 0),
          exceptionalInstallment:
            item.exceptionalInstallment == null
              ? null
              : Number(item.exceptionalInstallment),
          dailySalary: Number(item.dailySalary ?? 0),
          workingDays: Number(item.workingDays ?? 0),
          penaltyDays: Number(item.penaltyDays ?? 0),
          suggestedApprovedAmount: Number(item.suggestedApprovedAmount ?? 0),
          salaryMissing: Boolean(item.salaryMissing),
        };
      });

      setRows(mapped);
      setMonthMeta({
        month: context?.month ?? null,
        year: context?.year ?? null,
      });

      const defaults = {};
      const installmentDefaults = {};
      mapped.forEach((row) => {
        // HR must enter the approved disbursement amount (starts empty).
        defaults[row.id] = "";
        if (row.isExceptional) {
          installmentDefaults[row.id] = String(row.installments ?? 2);
        }
      });
      setApprovedAmounts(defaults);
      setEditedInstallments(installmentDefaults);
      setInstallmentSchedules({});
    } catch (err) {
      const message = err.response?.data?.message;
      setLoadError(
        Array.isArray(message)
          ? message.join("\n")
          : message || "تعذر جلب طلبات السلف",
      );
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAdvances();
  }, [loadAdvances]);

  const pendingForHr = useMemo(() => {
    let list = rows;
    if (isGm) {
      list = list.filter((row) => row.referredToGmAt);
    }
    if (!teamOnly) return list;
    return list.filter(
      (row) => row.userId && teamUserIds.has(String(row.userId)),
    );
  }, [rows, teamOnly, teamUserIds, isGm]);

  function handleApprovedAmountChange(rowId, value) {
    setApprovedAmounts((prev) => ({ ...prev, [rowId]: value }));
  }

  function handleInstallmentsChange(rowId, value, currentTotal) {
    setEditedInstallments((prev) => ({ ...prev, [rowId]: value }));
    const count = Number(value);
    const total = Number(currentTotal);
    if (!Number.isNaN(count) && count >= 2 && count <= 6 && total > 0) {
      setInstallmentSchedules((prev) => ({
        ...prev,
        [rowId]: splitInstallmentAmounts(total, count).map((amount) =>
          String(amount),
        ),
      }));
    }
  }

  function handleScheduleAmountChange(rowId, index, value) {
    setInstallmentSchedules((prev) => {
      const current = Array.isArray(prev[rowId]) ? [...prev[rowId]] : [];
      current[index] = value;
      return { ...prev, [rowId]: current };
    });
  }

  function openActionModal(advanceId, type) {
    if (type === "approve") {
      const raw = approvedAmounts[advanceId];
      const value = Number(raw);
      if (!raw || Number.isNaN(value) || value <= 0) {
        setActionError("أدخل مبلغاً معتمداً صحيحاً أكبر من صفر قبل الموافقة");
        return;
      }
      const row = pendingForHr.find((item) => item.id === advanceId);
      if (row?.isExceptional) {
        const installments = Number(
          editedInstallments[advanceId] ?? row.installments,
        );
        if (
          Number.isNaN(installments) ||
          installments < 2 ||
          installments > 6
        ) {
          setActionError("عدد الأقساط للسلفة الاستثنائية يجب أن يكون بين 2 و 6");
          return;
        }
        setInstallmentSchedules((prev) => ({
          ...prev,
          [advanceId]: scheduleForApproval(
            prev[advanceId],
            value,
            installments,
          ),
        }));
      }
    }
    setActionModal({ open: true, type, advanceId, notes: "" });
    setActionError("");
  }

  function closeActionModal() {
    setActionModal({ open: false, type: "", advanceId: null, notes: "" });
    setActionError("");
    setSubmitting(false);
  }

  async function handleConfirmAction() {
    const { type, notes, advanceId } = actionModal;
    if (type === "reject" && !notes.trim()) {
      setActionError("يجب إدخال سبب الرفض");
      return;
    }

    setSubmitting(true);
    setActionError("");
    try {
        if (type === "approve") {
          const approvedAmount = Number(approvedAmounts[advanceId]);
          if (Number.isNaN(approvedAmount) || approvedAmount <= 0) {
            setActionError("أدخل مبلغاً معتمداً صحيحاً أكبر من صفر");
            setSubmitting(false);
            return;
          }
          const row = pendingForHr.find((item) => item.id === advanceId);
          const payload = {
            notes: notes.trim() || undefined,
            approvedAmount,
          };
          if (row?.isExceptional) {
            const installments = Number(editedInstallments[advanceId]);
            if (
              Number.isNaN(installments) ||
              installments < 2 ||
              installments > 6
            ) {
              setActionError("عدد الأقساط للسلفة الاستثنائية يجب أن يكون بين 2 و 6");
              setSubmitting(false);
              return;
            }
            const scheduleStatus = scheduleMatchesLoan(
              installmentSchedules[advanceId],
              approvedAmount,
            );
            if (
              !scheduleStatus.matches ||
              scheduleStatus.amounts.length !== installments
            ) {
              setActionError("مجموع الأقساط لازم يساوي مبلغ السلفة");
              setSubmitting(false);
              return;
            }
            payload.installments = installments;
            payload.installmentAmounts = scheduleStatus.amounts.map((amount) =>
              Math.round(amount * 100) / 100,
            );
          }
          await approveAdvance(advanceId, payload);
        } else if (type === "refer") {
          await referAdvanceToGm(advanceId, {
            notes: notes.trim() || undefined,
          });
        } else {
          await rejectAdvance(advanceId, { notes: notes.trim() });
        }

      closeActionModal();
      await loadAdvances();
      refreshData?.({ silent: true, inboxOnly: true });
    } catch (err) {
      const message = err.response?.data?.message;
      setActionError(
        Array.isArray(message)
          ? message.join("\n")
          : message || "تعذر تنفيذ الإجراء على طلب السلفة",
      );
      setSubmitting(false);
    }
  }

  const selectedAdvance = pendingForHr.find(
    (row) => row.id === actionModal.advanceId,
  );
  const selectedInstallmentCount = selectedAdvance?.isExceptional
    ? Number(
        editedInstallments[selectedAdvance.id] ?? selectedAdvance.installments,
      )
    : Number(selectedAdvance?.installments || 1);
  const selectedApprovedAmount = Number(
    selectedAdvance ? approvedAmounts[selectedAdvance.id] : 0,
  );
  const selectedCapWarning =
    actionModal.type === "approve" && selectedAdvance
      ? salaryCapWarning(
          selectedAdvance,
          approvedAmounts[selectedAdvance.id],
        )
      : "";
  const selectedSchedule = selectedAdvance
    ? installmentSchedules[selectedAdvance.id] || []
    : [];
  const selectedScheduleStatus = scheduleMatchesLoan(
    selectedSchedule,
    selectedApprovedAmount,
  );
  const selectedMonths = deductionMonthLabels(selectedInstallmentCount);
  const scheduleBlocksApproval =
    actionModal.type === "approve" &&
    Boolean(selectedAdvance?.isExceptional) &&
    (!selectedScheduleStatus.matches ||
      selectedSchedule.length !== selectedInstallmentCount);

  const title = teamOnly ? "موافقات فريقي" : "موافقات السلف";
  const subtitle = teamOnly
    ? "طلبات السلف بانتظار موافقتك من الموظفين التابعين لك مباشرة فقط"
    : isGm
      ? "طلبات السلف التي أرسلتها لك الموارد البشرية فقط. يمكنك الموافقة أو الرفض وتعديل المبلغ المعتمد."
      : canApprove
        ? "طلبات السلف الواصلة لمرحلة الموارد البشرية — المبلغ الممكن صرفه = اليومية × (أيام العمل − 5 − أيام الجزاءات) − السلف المستلمة هذا الشهر، والمبلغ المعتمد للصرف هو ما يُصرف عند الموافقة"
        : "عرض طلبات السلف بمرحلة الموارد البشرية (اطلاع فقط — الموافقة للـ HR المسؤول)";
  const emptyMessage = teamOnly
    ? teamMembers?.length
      ? "لا توجد طلبات سلف من فريقك بانتظار موافقة الموارد البشرية حالياً"
      : "لا يوجد موظفون تابعون لك مباشرة حالياً"
    : isGm
      ? "لا توجد سلف أرسلتها الموارد البشرية بانتظار موافقتك"
      : "لا توجد طلبات سلف بانتظار موافقة الموارد البشرية حالياً";

  const periodLabel =
    monthMeta.month && monthMeta.year
      ? `${monthMeta.month}/${monthMeta.year}`
      : "الشهر الحالي";

  function resolveAdvanceEmployee(row) {
    if (!row) return null;
    const people = [
      ...(teamMembers || []),
      ...Object.values(employeesByBranch || {}).flat(),
    ];
    return (
      people.find(
        (emp) => row.employeeId && String(emp.id) === String(row.employeeId),
      ) ||
      people.find(
        (emp) => row.userId && String(emp.userId) === String(row.userId),
      ) ||
      people.find(
        (emp) =>
          row.employeeCode && String(emp.code) === String(row.employeeCode),
      ) ||
      null
    );
  }

  function openPunchDays(row) {
    const emp = resolveAdvanceEmployee(row);
    const employeeId = emp?.id || row?.employeeId;
    if (!employeeId) return;
    const { month, year } = calendarMonthYearFromDate(row.submittedAt);
    setPunchModal({
      employeeId,
      employeeName: emp?.name || row.employeeName || "موظف",
      employeeCode: emp?.code || row.employeeCode || "",
      month,
      year,
      highlightDate: toIsoDate(row.submittedAt),
    });
  }

  function handleExportExcel() {
    if (!pendingForHr.length) return;

    const excelRows = pendingForHr.map((row) => {
      const edited = approvedAmounts[row.id];
      const editedNum = Number(edited);
      const rowInstallments = row.isExceptional
        ? Number(editedInstallments[row.id] ?? row.installments)
        : 1;
      const exceptionalInstallment =
        row.isExceptional && !Number.isNaN(editedNum) && editedNum > 0
          ? editedNum / rowInstallments
          : row.exceptionalInstallment;
      const approvedAmount =
        edited !== "" && edited != null ? Number(edited) : NaN;
      const livePercentage = resolveSalaryPercentage(row, edited);

      return {
        "الفرع / الإدارة": row.branchName || row.departmentName || "",
        الكود: row.employeeCode || "",
        الاسم: row.employeeName || "",
        الوظيفة: row.jobTitle || "",
        "نوع السلفة": row.advanceKindLabel || "عادية",
        "تاريخ طلب السلفة": row.submittedAt
          ? formatDate(row.submittedAt)
          : "",
        "المبلغ المطلوب": Number(row.requestedAmount) || 0,
        "عدد الأقساط": row.isExceptional ? Number(row.installments) || 1 : 1,
        الضامن: row.isExceptional ? row.guarantorName || "" : "",
        السبب: row.reasonText || "",
        "السلف المستلمة هذا الشهر": Number(row.dueAdvancesAmount) || 0,
        "النسبة من الراتب":
          livePercentage == null ? "" : Number(livePercentage),
        "الحد الأقصى للسلف": Number(row.maxAdvanceAmount) || 0,
        "قسط السلفة الاستثنائية":
          row.isExceptional && exceptionalInstallment != null
            ? Number(exceptionalInstallment)
            : "",
        "المبلغ الممكن صرفه": Number(row.suggestedApprovedAmount) || 0,
        "المبلغ المعتمد للصرف": Number.isNaN(approvedAmount)
          ? ""
          : approvedAmount,
        "الراتب اليومي": Number(row.dailySalary) || 0,
        "أيام العمل": Number(row.workingDays) || 0,
        "أيام الجزاءات": Number(row.penaltyDays) || 0,
      };
    });

    const stamp = new Date().toISOString().slice(0, 10);
    exportExcel(excelRows, `موافقات-السلف-${stamp}`, "موافقات السلف");
  }

  return (
    <div className="hr-advance-approvals" dir="rtl">
      <div className="hr-advance-header">
        <div>
          <h2 className="hr-advance-title">{title}</h2>
          <p className="hr-advance-subtitle">{subtitle}</p>
        </div>
        <div className="hr-advance-header-actions">
          <button
            type="button"
            className="hr-advance-export"
            onClick={handleExportExcel}
            disabled={loading || pendingForHr.length === 0}
          >
            تصدير Excel
          </button>
          <button
            type="button"
            className="hr-advance-refresh"
            onClick={loadAdvances}
            disabled={loading}
          >
            تحديث
          </button>
        </div>
      </div>

      {loading ? (
        <div className="hr-advance-empty">جاري تحميل الطلبات...</div>
      ) : loadError ? (
        <div className="hr-advance-error-banner">{loadError}</div>
      ) : pendingForHr.length === 0 ? (
        <div className="hr-advance-empty">{emptyMessage}</div>
      ) : (
        <>
          <div className="hr-advance-table-wrap">
            <table className="hr-advance-table">
              <thead>
                <tr>
                  <th>الفرع / الإدارة</th>
                  <th>الكود</th>
                  <th>الاسم</th>
                  <th>الوظيفة</th>
                  <th>نوع السلفة</th>
                  <th>تاريخ طلب السلفة</th>
                  <th>المبلغ المطلوب</th>
                  <th>عدد الأقساط</th>
                  <th>الضامن</th>
                  <th>السبب</th>
                  <th>السلف المستلمة هذا الشهر</th>
                  <th>النسبة من الراتب</th>
                  <th>الحد الأقصى للسلف</th>
                  <th>خصم الأشهر</th>
                  <th className="hr-advance-col-possible">المبلغ الممكن صرفه</th>
                  <th className="hr-advance-col-approved">المبلغ المعتمد للصرف</th>
                  <th>إجراء</th>
                </tr>
              </thead>
              <tbody>
                {pendingForHr.map((row) => {
                  const edited = approvedAmounts[row.id];
                  const rowInstallments = row.isExceptional
                    ? Number(editedInstallments[row.id] ?? row.installments)
                    : 1;
                  const liveSalaryPercentage = resolveSalaryPercentage(
                    row,
                    edited,
                  );

                  return (
                    <tr key={row.id}>
                      <td data-label="الفرع / الإدارة">
                        {row.branchName || row.departmentName || "—"}
                      </td>
                      <td data-label="الكود">{row.employeeCode || "—"}</td>
                      <td className="hr-advance-name-td" data-label="الاسم">
                        <div className="hr-advance-name-cell">
                          {resolveAdvanceEmployee(row)?.id || row.employeeId ? (
                            <button
                              type="button"
                              className="hr-advance-name-btn"
                              onClick={() => openPunchDays(row)}
                              title="عرض أيام البصمة"
                            >
                              {row.employeeName || "موظف"}
                            </button>
                          ) : (
                            <span>{row.employeeName || "موظف"}</span>
                          )}
                          {row.id ? (
                            <AttachmentList
                              entityType="advance"
                              entityId={row.id}
                              showDelete={false}
                              compact
                            />
                          ) : null}
                        </div>
                      </td>
                      <td data-label="الوظيفة">{row.jobTitle || "—"}</td>
                      <td data-label="نوع السلفة">
                        <span
                          className={`hr-advance-kind ${
                            row.isExceptional ? "exceptional" : "ordinary"
                          }`}
                        >
                          {row.advanceKindLabel || "عادية"}
                        </span>
                      </td>
                      <td data-label="تاريخ طلب السلفة">
                        {formatDate(row.submittedAt)}
                      </td>
                      <td data-label="المبلغ المطلوب">
                        {formatAmount(row.requestedAmount)}
                      </td>
                      <td data-label="عدد الأقساط">
                        {row.isExceptional ? (
                          canApprove ? (
                            <input
                              type="number"
                              min="2"
                              max="6"
                              step="1"
                              className="hr-advance-approved-input hr-advance-installments-input"
                              value={editedInstallments[row.id] ?? row.installments}
                              onChange={(e) =>
                                handleInstallmentsChange(
                                  row.id,
                                  e.target.value,
                                  edited,
                                )
                              }
                              aria-label={`عدد الأقساط لـ ${row.employeeName || "موظف"}`}
                            />
                          ) : (
                            formatNumber(rowInstallments)
                          )
                        ) : (
                          "1"
                        )}
                      </td>
                      <td className="hr-advance-detail-cell" data-label="الضامن">
                        {row.isExceptional
                          ? row.guarantorName || "—"
                          : "—"}
                      </td>
                      <td className="hr-advance-reason-cell" data-label="السبب">
                        {row.reasonText || "—"}
                      </td>
                      <td data-label="السلف المستلمة هذا الشهر">
                        {formatAmount(row.dueAdvancesAmount)}
                      </td>
                      <td
                        data-label="النسبة من الراتب"
                        className={
                          exceedsSalaryCap(liveSalaryPercentage)
                            ? "hr-advance-over-cap"
                            : undefined
                        }
                      >
                        {formatPercent(liveSalaryPercentage)}
                      </td>
                      <td data-label="الحد الأقصى للسلف">
                        {row.monthlySalary > 0
                          ? formatAmount(row.maxAdvanceAmount)
                          : "—"}
                      </td>
                      <td data-label="خصم الأشهر">
                        {row.isExceptional
                          ? canApprove
                            ? "عند الموافقة"
                            : "—"
                          : "—"}
                      </td>
                      <td
                        className="hr-advance-col-possible"
                        data-label="المبلغ الممكن صرفه"
                      >
                        {row.salaryMissing &&
                        !(row.suggestedApprovedAmount > 0) ? (
                          <span className="hr-advance-hint">لا يوجد راتب</span>
                        ) : (
                          <div className="hr-advance-cell-value">
                            <span>{formatAmount(row.suggestedApprovedAmount)}</span>
                            <span className="hr-advance-hint">
                              {formatNumber(row.dailySalary)} × (
                              {formatNumber(row.workingDays)} − 5 −{" "}
                              {formatNumber(row.penaltyDays)}) −{" "}
                              {formatNumber(row.dueAdvancesAmount)}
                            </span>
                          </div>
                        )}
                      </td>
                      <td
                        className="hr-advance-col-approved"
                        data-label="المبلغ المعتمد للصرف"
                      >
                        {canApprove ? (
                          <div className="hr-advance-cell-value">
                            <input
                              type="number"
                              min="0.01"
                              step="0.01"
                              className="hr-advance-approved-input"
                              value={edited ?? ""}
                              placeholder="أدخل المبلغ"
                              onChange={(e) =>
                                handleApprovedAmountChange(row.id, e.target.value)
                              }
                              aria-label={`المبلغ المعتمد للصرف لـ ${row.employeeName || "موظف"}`}
                            />
                            {salaryCapWarning(row, edited) ? (
                              <span className="hr-advance-cap-warning">
                                يتجاوز {ADVANCE_SALARY_CAP_PERCENT}% من الراتب
                              </span>
                            ) : null}
                          </div>
                        ) : (
                          <span>
                            {edited
                              ? formatAmount(edited)
                              : "—"}
                          </span>
                        )}
                      </td>
                      <td className="hr-advance-actions-cell" data-label="إجراء">
                        {canApprove || canReferToGm ? (
                          <div className="hr-advance-row-actions">
                            {canApprove ? (
                              <>
                                <button
                                  type="button"
                                  className="hr-advance-btn reject"
                                  onClick={() => openActionModal(row.id, "reject")}
                                >
                                  رفض
                                </button>
                                <button
                                  type="button"
                                  className="hr-advance-btn approve"
                                  onClick={() => openActionModal(row.id, "approve")}
                                >
                                  موافقة
                                </button>
                              </>
                            ) : null}
                            {canReferToGm ? (
                              row.referredToGmAt ? (
                                <span className="hr-advance-sent-chip">
                                  أُرسلت للمدير العام
                                </span>
                              ) : (
                                <button
                                  type="button"
                                  className="hr-advance-btn refer"
                                  onClick={() => openActionModal(row.id, "refer")}
                                >
                                  إرسال للمدير العام
                                </button>
                              )
                            ) : null}
                          </div>
                        ) : (
                          <span className="hr-advance-hint">اطلاع فقط</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <p className="hr-advance-formula-note">
            السلف المستلمة هذا الشهر ({periodLabel}) = مجموع السلف التي استلمها
            الموظف نقداً خلال الشهر الحالي (تاريخ الاستلام، أو تاريخ الموافقة إن لم
            يُسجَّل استلام). النسبة من الراتب = المستلم ÷ إجمالي الراتب، والحد
            الأقصى للسلف = 50% من الراتب.
          </p>
          <p className="hr-advance-formula-note">
            معادلة المبلغ الممكن صرفه ({periodLabel}): الثابت × عدد أيام العمل −
            الثابت × 5 أيام − الثابت × عدد أيام الجزاءات − السلف المستلمة هذا
            الشهر — الثابت = اليومية (راتب شهري ÷ 30، أو اليومية المسجّلة للأسبوعي)، وأيام العمل = الحضور + الإجازات
            المعتمدة المدفوعة. المبلغ المعتمد للصرف يدخله الـ HR وهو المبلغ الذي
            يُصرف عند الموافقة، ويحدّث النسبة من الراتب فوراً.
          </p>

          {!actionModal.open && actionError ? (
            <p className="hr-advance-inline-error">{actionError}</p>
          ) : null}
        </>
      )}

      {actionModal.open && (
        <div className="hr-advance-modal-overlay" onClick={closeActionModal}>
          <div
            className={`hr-advance-modal${
              actionModal.type === "approve" && selectedAdvance?.isExceptional
                ? " wide"
                : ""
            }`}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="hr-advance-modal-title">
              {actionModal.type === "approve"
                ? "تأكيد الموافقة"
                : actionModal.type === "refer"
                  ? "إرسال للمدير العام"
                  : "تأكيد الرفض"}
            </h3>
            <p className="hr-advance-modal-subtitle">
              {selectedAdvance
                ? `${selectedAdvance.employeeName || "موظف"} — مطلوب: ${formatAmount(selectedAdvance.requestedAmount)}${
                    actionModal.type === "approve"
                      ? ` · معتمد: ${formatAmount(approvedAmounts[selectedAdvance.id])}`
                      : ""
                  }`
                : "تأكيد الإجراء على طلب السلفة"}
            </p>

            {selectedCapWarning ? (
              <p className="hr-advance-modal-cap-warning" role="status">
                {selectedCapWarning}
              </p>
            ) : null}

            {selectedAdvance ? (
              <dl className="hr-advance-modal-details">
                <div>
                  <dt>تاريخ طلب السلفة</dt>
                  <dd>{formatDate(selectedAdvance.submittedAt)}</dd>
                </div>
                <div>
                  <dt>نوع السلفة</dt>
                  <dd>{selectedAdvance.advanceKindLabel || "عادية"}</dd>
                </div>
                <div>
                  <dt>عدد الأقساط</dt>
                  <dd>
                    {selectedAdvance.isExceptional &&
                    actionModal.type === "approve" &&
                    canApprove ? (
                      <input
                        type="number"
                        min="2"
                        max="6"
                        step="1"
                        className="hr-advance-approved-input hr-advance-installments-input"
                        value={
                          editedInstallments[selectedAdvance.id] ??
                          selectedAdvance.installments
                        }
                        onChange={(e) =>
                          handleInstallmentsChange(
                            selectedAdvance.id,
                            e.target.value,
                            approvedAmounts[selectedAdvance.id],
                          )
                        }
                        disabled={submitting}
                        aria-label="عدد الأقساط بالشهور"
                      />
                    ) : selectedAdvance.isExceptional ? (
                      formatNumber(selectedInstallmentCount)
                    ) : (
                      formatNumber(selectedAdvance.installments || 1)
                    )}
                  </dd>
                </div>
                {selectedAdvance.isExceptional ? (
                  <div>
                    <dt>الضامن</dt>
                    <dd>{selectedAdvance.guarantorName || "—"}</dd>
                  </div>
                ) : null}
                {selectedAdvance.reasonText ? (
                  <div className="hr-advance-modal-details-full">
                    <dt>السبب</dt>
                    <dd>{selectedAdvance.reasonText}</dd>
                  </div>
                ) : null}
                {selectedAdvance.jobTitle ? (
                  <div>
                    <dt>الوظيفة</dt>
                    <dd>{selectedAdvance.jobTitle}</dd>
                  </div>
                ) : null}
                {selectedAdvance.branchName ? (
                  <div>
                    <dt>الفرع</dt>
                    <dd>{selectedAdvance.branchName}</dd>
                  </div>
                ) : selectedAdvance.departmentName ? (
                  <div>
                    <dt>الإدارة</dt>
                    <dd>{selectedAdvance.departmentName}</dd>
                  </div>
                ) : null}
              </dl>
            ) : null}

            {actionModal.type === "approve" && selectedAdvance?.isExceptional ? (
              <div className="hr-advance-schedule">
                <div className="hr-advance-schedule-head">
                  <span>خصم كل شهر</span>
                  <button
                    type="button"
                    className="hr-advance-schedule-even"
                    onClick={() =>
                      handleInstallmentsChange(
                        selectedAdvance.id,
                        String(selectedInstallmentCount),
                        selectedApprovedAmount,
                      )
                    }
                    disabled={submitting}
                  >
                    توزيع بالتساوي
                  </button>
                </div>
                <p className="hr-advance-schedule-note">
                  أول خصم في الشهر التالي للموافقة. مجموع الأشهر لازم يساوي مبلغ السلفة.
                </p>
                {selectedMonths.map((month, index) => (
                  <label key={month.key} className="hr-advance-schedule-row">
                    <span>{month.label}</span>
                    <input
                      type="number"
                      min="0.01"
                      step="0.01"
                      className="hr-advance-approved-input"
                      value={selectedSchedule[index] ?? ""}
                      onChange={(e) =>
                        handleScheduleAmountChange(
                          selectedAdvance.id,
                          index,
                          e.target.value,
                        )
                      }
                      disabled={submitting}
                      aria-label={`خصم ${month.label}`}
                    />
                  </label>
                ))}
                <p
                  className={`hr-advance-schedule-total ${
                    selectedScheduleStatus.matches ? "match" : "mismatch"
                  }`}
                >
                  المجموع {formatAmount(selectedScheduleStatus.sum)} من{" "}
                  {formatAmount(selectedScheduleStatus.target)}
                  {selectedScheduleStatus.matches
                    ? ""
                    : " — المجموع لازم يساوي مبلغ السلفة"}
                </p>
              </div>
            ) : null}

            <div className="hr-advance-modal-field">
              <label className="hr-advance-modal-label">
                {actionModal.type === "reject"
                  ? "سبب الرفض (مطلوب)"
                  : actionModal.type === "refer"
                    ? "ملاحظة للمدير العام (اختياري)"
                    : "ملاحظات (اختياري)"}
              </label>
              <textarea
                className={`hr-advance-modal-textarea ${
                  actionError && actionModal.type === "reject" ? "has-error" : ""
                }`}
                rows={4}
                value={actionModal.notes}
                onChange={(e) =>
                  setActionModal((prev) => ({ ...prev, notes: e.target.value }))
                }
                placeholder={
                  actionModal.type === "reject"
                    ? "اكتب سبب الرفض..."
                    : "أضف ملاحظات إن وجدت..."
                }
                disabled={submitting}
              />
            </div>

            {actionError ? (
              <p className="hr-advance-modal-error">{actionError}</p>
            ) : null}

            <div className="hr-advance-modal-buttons">
              <button
                type="button"
                className="hr-advance-modal-btn secondary"
                onClick={closeActionModal}
                disabled={submitting}
              >
                إلغاء
              </button>
              <button
                type="button"
                className={`hr-advance-modal-btn ${actionModal.type}`}
                onClick={handleConfirmAction}
                disabled={submitting || scheduleBlocksApproval}
              >
                {submitting
                  ? "جاري التنفيذ..."
                  : actionModal.type === "approve"
                    ? "تأكيد الموافقة"
                    : actionModal.type === "refer"
                      ? "إرسال"
                      : "تأكيد الرفض"}
              </button>
            </div>
          </div>
        </div>
      )}

      {punchModal ? (
        <EmployeePunchDaysModal
          employeeId={punchModal.employeeId}
          employeeName={punchModal.employeeName}
          employeeCode={punchModal.employeeCode}
          month={punchModal.month}
          year={punchModal.year}
          highlightDate={punchModal.highlightDate}
          onClose={() => setPunchModal(null)}
        />
      ) : null}
    </div>
  );
}
